import { computed, ref, watch } from 'vue';
import { getHostRouter } from '@Src/mfe.config';
import { startParticipantInitiatedPaymentApi } from '@Api/dashboard.api';
import { useDashboardStore } from '@Stores/dashboard.store';
import { canRoleInitiatePurpose } from '@Src/shared/utils/participantRole';
import { withCompanySlug } from '@Src/shared/utils/withCompanySlug';
import {
    OTHER_PURPOSE,
    type OtherPaymentSelection,
    type OtherPaymentType,
} from '@Constants/otherPaymentType.constants';
import { ParticipantPaymentTracking } from '@Analytics';

/**
 * Presentation for each purpose the BFF can offer. The EMD and cash-to-close copy
 * is taken verbatim from the certifid-100-vision prototype's en.json
 * (portal.buyerInitiated.sheet.*). Other is new and has no prototype copy.
 *
 * Icons are substitutions, not matches. The prototype uses lucide HandCoinsIcon
 * for earnest money and KeyRoundIcon for cash to close; uilib has neither, so
 * this maps to the nearest available semantics: a protected-money mark for the
 * good-faith deposit, and a home mark for the funds that close on the house.
 * Worth a design confirmation.
 *
 * A purpose the BFF sends that is absent here is dropped rather than shown under
 * a fabricated label, matching how payments-mfe treats unknown server purposes.
 */
const PURPOSE_PRESENTATION: Record<string, { label: string; helper: string; icon: string }> = {
    EMD: {
        label: 'Earnest money deposit',
        helper: 'The good-faith deposit to secure the property.',
        icon: 'cd-ic-money-shield',
    },
    CashToClose: {
        label: 'Cash to close',
        helper: 'The remaining funds due to close on your home.',
        icon: 'cd-ic-home',
    },
    [OTHER_PURPOSE]: {
        label: 'Other',
        helper: 'Option fees, deposits, HOA fees and other payments on this file.',
        icon: 'cd-ic-more-circle',
    },
};

/**
 * Closing-fund purposes lead, earnest money first, so visual weight tracks
 * consequence. This is the prototype's `closingIntents` order, and it overrides
 * whatever order the BFF happens to send. Other follows them.
 */
const PURPOSE_ORDER = ['EMD', 'CashToClose', OTHER_PURPOSE];

export interface InitiablePurpose {
    value: string;
    label: string;
    helper: string;
    icon: string;
}

// Module scoped on purpose. See the impression watcher below.
const dealsAlreadyReportedAsViewed = new Set<string>();

export function useParticipantInitiatedPayment() {
    const hostRouter = getHostRouter();
    const dashboardStore = useDashboardStore();

    const isSheetOpen = ref(false);
    const isStarting = ref(false);
    const startError = ref<string | null>(null);

    const purposes = computed<InitiablePurpose[]>(() => {
        const deal = dashboardStore.activeDeal;
        const offered = deal?.participantInitiablePurposes ?? [];
        return PURPOSE_ORDER
            .filter((value) => offered.includes(value))
            .filter((value) => canRoleInitiatePurpose(deal?.participantRole, value))
            .map((value) => ({ value, ...PURPOSE_PRESENTATION[value] }));
    });

    // dashboard-bff applies both kill switches (payments, Other) server-side by leaving purposes out.
    const canInitiatePayment = computed(() => purposes.value.length > 0);

    // First true edge is the impression; the module-scoped set keeps it to once per deal per session.
    watch(canInitiatePayment, (ready) => {
        const dealId = dashboardStore.activeDeal?.dealId ?? null;
        if (!ready || !dealId || dealsAlreadyReportedAsViewed.has(dealId)) return;
        dealsAlreadyReportedAsViewed.add(dealId);
        ParticipantPaymentTracking.trackEntryViewed(
            purposes.value.map((purpose) => purpose.value));
    }, { immediate: true });

    const openSheet = () => {
        startError.value = null;
        isSheetOpen.value = true;
        ParticipantPaymentTracking.trackPurposeSheetOpened();
    };

    const closeSheet = () => {
        isSheetOpen.value = false;
    };

    /**
     * The error renders on the card, which sits behind the sheet, so the sheet has
     * to close or the participant sees the rows re-enable and no message at all.
     */
    const failStart = (purpose: string, otherType?: OtherPaymentType) => {
        isSheetOpen.value = false;
        startError.value = 'Unable to start your payment. Please try again.';
        ParticipantPaymentTracking.trackStartFailed(purpose, otherType);
    };

    const startPayment = async (purpose: string, other?: OtherPaymentSelection) => {
        const dealId = dashboardStore.activeDeal?.dealId;
        if (!hostRouter || !dealId || isStarting.value) return;

        // The sheet always collects a type before emitting Other, so a missing one is a caller bug.
        if (purpose === OTHER_PURPOSE && !other) {
            console.error('[Dashboard MFE] Other payment started without a payment type. The purpose sheet must collect one before it emits the selection.');
            failStart(purpose);
            return;
        }

        isStarting.value = true;
        startError.value = null;
        ParticipantPaymentTracking.trackPurposeSelected(purpose, other?.type);

        try {
            const nav = await startParticipantInitiatedPaymentApi(dealId, purpose, other);
            if (!nav?.path) {
                failStart(purpose, other?.type);
                return;
            }

            const slug = hostRouter.currentRoute.value.params.companySlug as string;
            isSheetOpen.value = false;
            hostRouter.push({
                path: withCompanySlug(nav.path, slug),
                query: { source: 'dashboard' },
            });
        } catch (error) {
            console.error('[Dashboard MFE] Failed to start participant-initiated payment:', error);
            failStart(purpose, other?.type);
        } finally {
            isStarting.value = false;
        }
    };

    return {
        purposes,
        canInitiatePayment,
        isSheetOpen,
        isStarting,
        startError,
        openSheet,
        closeSheet,
        startPayment,
    };
}
